import { observer } from 'mobx-react-lite'
import {
  CalendarDropdown,
  FullPageWrapper,
  Typography,
  SpinnerLoader,
  type ActionItem,
} from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import {
  ConversionsAnalyticsListProvider,
  useConversionsAnalyticsListContext,
} from 'pages/analytics/pages/conversions/context/conversionsAnalyticsListContext'
import { AnalyticsHeader, TrendLabel, AnalyticsEmptyState } from 'pages/analytics/ui'
import {
  copySnippet,
  createConversion,
} from 'pages/analytics/pages/conversions/ui/ConversionActions/conversionActions'
import { ConversionsTable } from './ConversionsTable'
import styles from './styles.module.scss'

const ConversionsAnalyticsListPageContent = observer(() => {
  const {
    calendarFilterStore,
    getStatisticByKey,
    pageLoading,
    openExportModal,
    hasStatistic,
    conversionsTableStore,
  } = useConversionsAnalyticsListContext()
  const { loadConversions, conversionsRows } = conversionsTableStore
  const entities = useEntitiesContext()

  const totalStat = getStatisticByKey('total')
  const avgConversionRateStat = getStatisticByKey('avg_conversion_rate')
  const totalConversionRevenueStat = getStatisticByKey('total_conversion_revenue')

  const conversionsActions: ActionItem[] = [
    {
      text: 'New conversion',
      icon: 'add',
      buttonProps: {
        contained: 'primary',
      },
      onAction: () => createConversion(() => loadConversions()),
    },
    {
      text: 'Copy snippet',
      icon: 'copy',
      onAction: () => copySnippet(entities),
    },
    {
      text: 'Export data',
      icon: 'download',
      onAction: openExportModal,
    },
  ]

  const renderContent = () => {
    if (pageLoading) {
      return (
        <div className={styles.fullPageContainer}>
          <SpinnerLoader ariaLabel={'ConversionsAnalyticsListPage'} />
        </div>
      )
    }

    if (!hasStatistic && !conversionsRows.length) {
      return (
        <div className={styles.fullPageContainer}>
          <AnalyticsEmptyState />
        </div>
      )
    }

    return (
      <>
        <div className={styles.kpiSection}>
          <div className={styles.kpiItem}>
            <Typography variant={'body-rg-regular'} color={'tertiary'} ariaLabel={'TotalTitle'}>
              Total conversions
            </Typography>
            <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'TotalValue'}>
              {totalStat?.value?.toLocaleString() || '0'}
            </Typography>
            {totalStat?.trend && <TrendLabel trend={totalStat.trend} displayMode='percent' />}
          </div>
          <div className={styles.kpiDivider}></div>
          <div className={styles.kpiItem}>
            <Typography
              variant={'body-rg-regular'}
              color={'tertiary'}
              ariaLabel={'AvgConversionRateTitle'}
            >
              Avg conversion rate
            </Typography>
            <Typography
              variant={'heading-lg'}
              color={'primary'}
              ariaLabel={'AvgConversionRateValue'}
            >
              {avgConversionRateStat?.value?.toLocaleString() || '0'}%
            </Typography>
            {avgConversionRateStat?.trend && (
              <TrendLabel trend={avgConversionRateStat.trend} displayMode='percent' />
            )}
          </div>
          <div className={styles.kpiDivider}></div>
          <div className={styles.kpiItem}>
            <Typography
              variant={'body-rg-regular'}
              color={'tertiary'}
              ariaLabel={'TotalConversionRevenueTitle'}
            >
              Total conversion revenue
            </Typography>
            <Typography
              variant={'heading-lg'}
              color={'primary'}
              ariaLabel={'TotalConversionRevenueValue'}
            >
              ${totalConversionRevenueStat?.value?.toLocaleString() || '0'}
            </Typography>
            {totalConversionRevenueStat?.trend && (
              <TrendLabel trend={totalConversionRevenueStat.trend} displayMode='percent' />
            )}
          </div>
        </div>

        <div className={styles.tableSection}>
          <ConversionsTable />
        </div>
      </>
    )
  }

  return (
    <FullPageWrapper>
      <AnalyticsHeader title='Conversions' actions={conversionsActions} />

      <div className={styles.filtersRow}>
        <CalendarDropdown store={calendarFilterStore} />
      </div>

      {renderContent()}
    </FullPageWrapper>
  )
})

export const ConversionsAnalyticsListPage = () => (
  <ConversionsAnalyticsListProvider>
    <ConversionsAnalyticsListPageContent />
  </ConversionsAnalyticsListProvider>
)
